import React, { useState, useEffect } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormTextArea } from "../FormComponents";
import { requiredValidator } from "../Validators";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { Checkbox, RadioGroup } from "@progress/kendo-react-inputs";
import { GROUP_TYPE_OPTIONS, GroupType } from "../../../data/groupData";
import { getEmployeeDropdown } from "../../../data/employeeData";
import { getUserRoles } from "../../../data/userData";
import { dropDown } from "../../../data/interfaces";
import { UserRoleOption } from "../../../data/userData";

interface AddGroupFormProps {
  onSubmit: (dataItem: any) => void;
  onCancel?: () => void;
  // Only an administrator may give a group Portal.Administrator.
  canGrantAdmin?: boolean;
}

const AddGroupForm = ({ onSubmit, onCancel, canGrantAdmin = false }: AddGroupFormProps) => {
  const [employees, setEmployees] = useState<dropDown[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<dropDown[]>([]);
  const [roles, setRoles] = useState<UserRoleOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [groupType, setGroupType] = useState<GroupType>("Security");

  useEffect(() => {
    loadEmployees();
    loadRoles();
  }, []);

  const loadEmployees = async () => {
    try {
      const data = await getEmployeeDropdown("");
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await getUserRoles();
      setRoles(data);
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const handleSubmit = (dataItem: any) => {
    // Format data for API
    const formData = {
      name: dataItem.name,
      description: dataItem.description || "",
      groupType,
      employeeIds: selectedEmployees.length > 0 ? selectedEmployees : null,
      // A distribution group grants no roles.
      roleIds: groupType === "Security" && selectedRoles.length > 0 ? selectedRoles : null,
    };
    onSubmit(formData);
  };

  const handleEmployeeChange = (event: any) => {
    const values = event.target.value.map((item: dropDown) => item.value);
    setSelectedEmployees(values);
  };

  const handleEmployeeFilterChange = (event: any) => {
    const filtered = filterBy(employees, {
      logic: 'and',
      filters: [{
        field: 'text',
        operator: 'contains',
        value: event.filter.value
      }]
    });
    setFilteredEmployees(filtered);
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        name: "",
        description: "",
      }}
      render={(formRenderProps) => (
        <FormElement style={{ padding: "1rem", marginBottom: "5rem" }}>
          <fieldset className={"k-form-fieldset"}>
            <div className="form__subtitle">
              Add group details:
              <button
                className="form__reset__button"
                type="button"
                onClick={() => {
                  formRenderProps.onFormReset();
                  setSelectedEmployees([]);
                  setSelectedRoles([]);
                  setGroupType("Security");
                  setFilteredEmployees(employees);
                }}
              >
                Clear fields
              </button>
            </div>

            <Field
              name={"name"}
              component={FormInput}
              label={"Group Name: "}
              placeholder={"Enter group name"}
              validator={requiredValidator}
            />

            <Field
              name={"description"}
              component={FormTextArea}
              label={"Description: "}
              placeholder={"Enter description"}
            />

            <div className="k-form-field">
              <label className="k-label">Group type:</label>
              <RadioGroup
                data={GROUP_TYPE_OPTIONS}
                value={groupType}
                onChange={(e) => setGroupType(e.value)}
              />
            </div>

            <div className="k-form-field">
              <label className="k-label">Employees:</label>
              <MultiSelect
                data={filteredEmployees}
                value={employees.filter(e => selectedEmployees.includes(e.value))}
                onChange={handleEmployeeChange}
                onFilterChange={handleEmployeeFilterChange}
                textField="text"
                dataItemKey="value"
                placeholder="Select employees..."
                filterable={true}
                style={{ width: "100%" }}
              />
            </div>

            {groupType === "Security" && (
            <div className="k-form-field">
              <label className="k-label">Roles:</label>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                border: '1px solid #ccc', 
                padding: '10px',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9'
              }}>
                {roles
                  .sort((a, b) => {
                    const labelA = `${a.area} - ${a.permission}`;
                    const labelB = `${b.area} - ${b.permission}`;
                    return labelA.localeCompare(labelB);
                  })
                  .map(role => (
                  <div key={role.roleId} style={{ marginBottom: '8px' }}>
                    <Checkbox
                      value={selectedRoles.includes(role.roleId)}
                      disabled={!canGrantAdmin && `${role.area}.${role.permission}` === 'Portal.Administrator'}
                      onChange={(e) => {
                        if (e.value) {
                          setSelectedRoles([...selectedRoles, role.roleId]);
                        } else {
                          setSelectedRoles(selectedRoles.filter(id => id !== role.roleId));
                        }
                      }}
                      label={`${role.area} - ${role.permission}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            )}
          </fieldset>

          <div className="button__container">
            <button
              type="submit"
              className="form__edit__button edit"
              disabled={!formRenderProps.allowSubmit}
            >
              Save Group
            </button>
          </div>
        </FormElement>
      )}
    />
  );
};

export default AddGroupForm;
